

import React from 'react';
import { useDashboardData } from '@/hooks/useDashboardData';
import { StatsSection, ChartsSection, ListsSection } from './sections';
import { FinancialWidget } from './widgets';
import { formatTime } from '@/lib/dateFormat';

export function DashboardGrid() {
  const { data, loading, error, refresh } = useDashboardData({
    refreshInterval: 30000,
    autoRefresh: true,
  });

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 sm:p-6">
        <p className="text-sm text-red-800 mb-3">{error}</p>
        <button
          onClick={refresh}
          className="text-sm px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const stats = data.stats || {};
  
  // Calculate consistent values for display
  const totalRevenue = stats.total_revenue || 0;
  const operationalExpenses = stats.operational_expenses || 0;
  const vendorPayments = stats.vendor_payments || 0;
  const totalExpenses = operationalExpenses + vendorPayments;
  const calculatedNetProfit = totalRevenue - totalExpenses;
  
  // Use backend net_profit when available; only fall back to frontend calc if missing
  const backendNetProfit = stats.net_profit;
  
  // Build consistent display stats — prefer backend values, fill gaps with frontend calculations
  const displayStats = {
    ...stats,
    net_profit: backendNetProfit ?? calculatedNetProfit,
    total_cost: stats.total_cost ?? totalExpenses,
  };

  return (
    <div className="space-y-6">
      <StatsSection stats={displayStats} loading={loading} />

      <FinancialWidget
        metrics={{
          totalRevenue: totalRevenue,
          totalExpenses: totalExpenses,
          operationalExpenses: operationalExpenses,
          vendorPayments: vendorPayments,
          netProfit: displayStats.net_profit,
          pendingPayments: stats.pending_invoices || 0,
        }}
        loading={loading}
      />

      <ChartsSection chartData={data.chartData} loading={loading} />

      <ListsSection
        events={data.events}
        quotations={data.quotations}
        tasks={data.tasks}
        stats={displayStats}
        loading={loading}
      />

      <div className="text-xs text-gray-500 text-center">
        Last updated: {formatTime(data.lastUpdated)}
        {' '}
        <button
          onClick={refresh}
          className="text-blue-600 hover:text-blue-700 ml-2"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
